import { Alert, Box, Card, Chip, Skeleton, Typography } from '@mui/material';
import { Lock } from 'lucide-react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import GameHeaderRow from '@/shared/components/GameHeaderRow';
import type { Game } from '@/features/games/types/Games';
import { cx } from '@/styles/muiTheme';

import { useGameWscStreams } from '../hooks/wscStreamQueries';
import { AddWscStreamForm } from './AddWscStreamForm';
import { WSC_STREAM_GRID, WSC_STREAM_GRID_GAP, WscStreamRow } from './WscStreamRow';

/**
 * Column labels for the stream rows. Same track list as the rows themselves —
 * imported, not restated, so the two grids cannot drift out of alignment.
 * The trailing empty cell reserves the actions column.
 */
const COLUMNS = ['Status', 'Camera', 'Scheduled', 'Instance', 'Endpoint', ''];

interface Props {
  game: Game;
}

/**
 * Per-game card for the WSC Streams page: the shared game envelope on top, with
 * a streams management panel directly beneath it.
 */
export function WscGameCard({ game }: Props) {
  const { data: streams, isLoading, isError, error } = useGameWscStreams(game.uniqueId);
  // LD flag — when ON, the readonly gate is bypassed so operators can edit
  // streams on live / recorded games. Intended for dev1 testing; OFF in stg/prd.
  const { wscStreamsAllowAllEdits } = useFlags();

  const gameStatus = (game as any).status || 'recorded';

  // Streams are only editable while the game is still scheduled. Once a
  // game is live / recorded / ended, its streams are display-only — no
  // add form, no per-row delete buttons. The pipeline behind the scenes
  // still progresses (LAUNCHING → RUNNING → ENDED), it's just that the
  // operator can't intervene from this page anymore.
  // The `wscStreamsAllowAllEdits` flag bypasses this gate (dev-only override).
  const isReadOnly = !wscStreamsAllowAllEdits && gameStatus !== 'scheduled';

  // 'recorded' is the terminal game state — the game is over, so there is
  // nothing a new stream could capture. Unlike the read-only gate above, the
  // LD flag deliberately does NOT bypass this one: letting an operator edit
  // streams on a live game is a useful dev override, but scheduling a stream
  // for a finished game is never correct, in any environment.
  const isFinal = gameStatus === 'recorded';

  return (
    <Card variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      <GameHeaderRow
        game={game}
        endAdornment={
          isReadOnly && (
            <Chip
              icon={<Lock size={12} />}
              label="Read-only"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          )
        }
      />

      {/* WSC Streams panel */}
      <Box>
        {isLoading && (
          <Box sx={{ px: 2, py: 1 }}>
            <Skeleton variant="rectangular" height={36} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={36} />
          </Box>
        )}

        {isError && (
          <Alert severity="error" sx={{ mx: 2, my: 1 }}>
            Failed to load streams: {(error as any)?.message ?? 'unknown error'}
          </Alert>
        )}

        {!isLoading && !isError && streams && streams.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
            {isFinal
              ? 'No streams were scheduled for this game.'
              : 'No streams scheduled for this game yet.'}
          </Typography>
        )}

        {!isLoading && !isError && streams && streams.length > 0 && (
          <Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: WSC_STREAM_GRID,
                gap: WSC_STREAM_GRID_GAP,
                alignItems: 'center',
                px: '20px',
                py: '8px',
                borderBottom: '1px solid',
                borderColor: cx.border.subtle,
                color: cx.text.muted,
                fontSize: 10,
                lineHeight: '14px',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              {COLUMNS.map((label, i) => (
                <Box key={label || `spacer-${i}`}>{label}</Box>
              ))}
            </Box>

            {streams.map((s) => (
              <WscStreamRow
                key={s.id}
                stream={s}
                gameUuid={game.uniqueId}
                readOnly={isReadOnly}
                gameIsFinal={isFinal}
              />
            ))}
          </Box>
        )}

        {!isReadOnly && !isFinal && <AddWscStreamForm gameUuid={game.uniqueId} />}
      </Box>
    </Card>
  );
}
